import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ShieldCheck } from "lucide-react";
import { SECTIONS, PRIVACY_LAST_UPDATED } from "@/pages/PrivacyPolicy";

/**
 * PrivacyPolicyModal — opens the full Privacy Policy inside a shadcn Dialog.
 *
 * Renders the exact same SECTIONS array as the standalone /privacy-policy
 * page (single source of truth — no copy/paste drift).
 *
 * Required for stricter compliance classifiers (Google Ads health-site
 * policy and similar) which need a visible, hyperlinked Privacy Policy at
 * the point of data capture. A modal keeps the user inside the form flow
 * (no lost form data, no broken funnel) while still satisfying the visible-
 * disclosure requirement.
 */
export default function PrivacyPolicyModal({ open, onOpenChange }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[88vh] overflow-hidden p-0 bg-white"
        data-testid="privacy-policy-modal"
      >
        <DialogHeader className="px-6 md:px-8 py-5 border-b border-[#E2E8F0] bg-[#0F172A] text-white">
          <div className="inline-flex items-center gap-2 px-2.5 py-1 border border-[#F97316]/40 w-fit mb-3">
            <ShieldCheck className="w-3.5 h-3.5 text-[#F97316]" strokeWidth={2} />
            <span className="font-mono text-[10px] uppercase tracking-widest text-[#F97316]">Your Privacy Matters</span>
          </div>
          <DialogTitle className="text-xl md:text-2xl font-bold text-white tracking-tight">Privacy Policy</DialogTitle>
          <p className="text-[#94A3B8] text-xs mt-1">Last updated: {PRIVACY_LAST_UPDATED}</p>
        </DialogHeader>

        <div className="overflow-y-auto px-6 md:px-8 py-6 max-h-[calc(88vh-130px)]" data-testid="privacy-policy-modal-body">
          {SECTIONS.map((s) => (
            <div key={s.id} className="mb-9" data-testid={`privacy-modal-section-${s.id}`}>
              <h2 className="text-base md:text-lg font-bold text-[#0F172A] mb-3 tracking-tight">{s.title}</h2>
              <div className="prose prose-sm max-w-none text-[#475569] [&_p]:leading-relaxed [&_p]:mb-3 [&_h3]:text-sm [&_h3]:font-semibold [&_h3]:text-[#0F172A] [&_h3]:mt-4 [&_h3]:mb-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1.5 [&_ul]:mb-3 [&_li]:text-xs md:[&_li]:text-sm">
                {s.body}
              </div>
            </div>
          ))}

          <div className="mt-8 pt-6 border-t border-[#E2E8F0] text-center">
            <p className="text-[11px] text-[#94A3B8]">
              © {new Date().getFullYear()} Ezeefit Modular Furniture Private Limited. GSTIN: 27AADCE6117G1ZT.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
